import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { APILifeInsuranceForm } from 'types/api.types';


export interface LifeInsuranceFormState {
  data: APILifeInsuranceForm;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  validationErrors: Record<string, any>;
  isDataSet: boolean;
  hasUnsavedChanges: boolean;
  isDataSaved: boolean;
  wipStatus: number | null;
  missingMandatoryFields: {
    name: string;
    section: string;
  }[];
  premiumConfirmed: boolean;
}

const initialState: LifeInsuranceFormState = {
  data: {
    first_name: '',
    last_name: '',
    date_of_birth: null,
    phone_number: '',
    email: '',
    gender: '',
    new_client_or_existing: 'new',
    referral: false,
    type_of_policy: '',
    disclosure_and_attestation: {
      broker_name: '',
    },
  },
  isLoading: false,
  isSaving: false,
  error: null,
  validationErrors: {},
  isDataSet: false,
  hasUnsavedChanges: false,
  isDataSaved: false,
  wipStatus: null,
  missingMandatoryFields: [],
  premiumConfirmed: false,
};

const lifeInsuranceFormSlice = createSlice({
  name: 'lifeInsuranceForm',
  initialState,
  reducers: {
    setClientFirstName: (state, action: PayloadAction<string>) => {
      state.data.first_name = action.payload;
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    setClientLastName: (state, action: PayloadAction<string>) => {
      state.data.last_name = action.payload;
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    setClientDateOfBirth: (state, action: PayloadAction<string>) => {
      state.data.date_of_birth = action.payload || null;
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    setClientPhoneNumber: (state, action: PayloadAction<string>) => {
      state.data.phone_number = action.payload;
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    setClientEmail: (state, action: PayloadAction<string>) => {
      state.data.email = action.payload;
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    setClientGender: (state, action: PayloadAction<string>) => {
      state.data.gender = action.payload;
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    setClientType: (state, action: PayloadAction<'new' | 'existing'>) => {
      state.data.new_client_or_existing = action.payload;
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    setReferral: (state, action: PayloadAction<boolean>) => {
      state.data.referral = action.payload;
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    setTypeOfPolicy: (state, action: PayloadAction<string>) => {
      const newTypeOfPolicy = action.payload;
      const previousTypeOfPolicy = state.data.type_of_policy || '';
      
      // Parse selected policy types
      const previousTypes = previousTypeOfPolicy
        ? previousTypeOfPolicy.split(',').map((t) => t.trim()).filter(Boolean)
        : [];
      const newTypes = newTypeOfPolicy
        ? newTypeOfPolicy.split(',').map((t) => t.trim()).filter(Boolean)
        : [];
      
      // Map product type names to their array keys
      const productTypeMap: Record<string, keyof APILifeInsuranceForm> = {
        'Life insurance': 'life_insurance',
        'Critical Illness': 'critical_illness',
        'Disability': 'disability',
        'Segregated Funds': 'segregated_funds',
        'Group Benefits': 'group_benefits',
        'Group Retirement Services': 'group_retirement_services',
        'Travel': 'travel',
        'Individual health and dental': 'individual_health_dental',
        'Other': 'other',
      };
      
      // Clear arrays for products that are no longer selected
      previousTypes.forEach((productType) => {
        if (!newTypes.includes(productType)) {
          const arrayKey = productTypeMap[productType];
          if (arrayKey) {
            (state.data as any)[arrayKey] = [];
          }
        }
      });
      
      // Ensure arrays exist for newly selected products (if they don't exist or are undefined)
      newTypes.forEach((productType) => {
        const arrayKey = productTypeMap[productType];
        if (arrayKey && (!state.data[arrayKey] || !Array.isArray(state.data[arrayKey]))) {
          (state.data as any)[arrayKey] = [];
        }
      });
      
      state.data.type_of_policy = newTypeOfPolicy;
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    updatePolicySection: (
      state,
      action: PayloadAction<{
        section: string;
        index: number;
        key: string;
        value: any;
      }>,
    ) => {
      const { section, index, key, value } = action.payload;
      if (!state.data[section as keyof APILifeInsuranceForm]) {
        (state.data as any)[section] = [];
      }
      const sectionArray = (state.data as any)[section];
      if (!Array.isArray(sectionArray)) {
        (state.data as any)[section] = [];
      }
      if (!sectionArray[index]) {
        sectionArray[index] = {};
      }
      sectionArray[index] = {
        ...sectionArray[index],
        [key]: value,
      };
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    updateProductAgnosticField: (
      state,
      action: PayloadAction<{ key: string; value: any }>,
    ) => {
      (state.data as any)[action.payload.key] = action.payload.value;
      // Reset premium confirmation if premium_amount drops to or below threshold
      if (action.payload.key === 'premium_amount') {
        const value = parseFloat(action.payload.value || '0');
        if (value <= 10000) {
          state.premiumConfirmed = false;
        }
      }
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    setPremiumConfirmed: (state, action: PayloadAction<boolean>) => {
      state.premiumConfirmed = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setValidationErrors: (
      state,
      action: PayloadAction<Record<string, any>>,
    ) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = {};
    },
    setDataSet: (state, action: PayloadAction<boolean>) => {
      state.isDataSet = action.payload;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
    changeIsDataSaved: (state, action: PayloadAction<boolean>) => {
      state.isDataSaved = action.payload;
    },
    setWipStatus: (state, action: PayloadAction<number | null>) => {
      state.wipStatus = action.payload;
    },
    changeDisclosureAndAttestation: (
      state,
      action: PayloadAction<{
        name: string;
        value: any;
      }>,
    ) => {
      state.data.disclosure_and_attestation = {
        ...state.data.disclosure_and_attestation,
        [action.payload.name]: action.payload.value,
      };
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    setAdditionalBrokerNote: (state, action: PayloadAction<string>) => {
      state.data.additional_broker_note = action.payload;
      state.hasUnsavedChanges = true;
      state.isDataSaved = false;
    },
    resetForm: () => {
      return initialState;
    },
    addMissingMandatoryField: (
      state,
      action: PayloadAction<{
        name: string;
        section: string;
      }>,
    ) => {
      const fieldExists = state.missingMandatoryFields.some(
        (field) => field.name === action.payload.name,
      );

      if (!fieldExists) {
        state.missingMandatoryFields.push({
          name: action.payload.name,
          section: action.payload.section,
        });
      }
    },
    removeMissingMandatoryField: (state, action: PayloadAction<string>) => {
      state.missingMandatoryFields = state.missingMandatoryFields.filter(
        (field) => field.name !== action.payload,
      );
    },
    initializeForm: (
      state,
      action: PayloadAction<Partial<APILifeInsuranceForm>>,
    ) => {
      const payload = action.payload;
      
      // Helper function to convert object to array if needed
      const ensureArray = (data: any) => {
        if (!data) return [];
        return Array.isArray(data) ? data : [data];
      };

      // Parse selected policy types from type_of_policy
      const selectedTypes = payload.type_of_policy
        ? payload.type_of_policy.split(',').map((t) => t.trim()).filter(Boolean)
        : [];

      // Helper function to get array for a product type
      const getProductArray = (
        productType: string,
        payloadKey: keyof APILifeInsuranceForm,
      ) => {
        // If this product type is selected, use data from payload or empty array
        if (selectedTypes.includes(productType)) {
          return payload[payloadKey] ? ensureArray(payload[payloadKey]) : [];
        }
        // If not selected, return empty array
        return [];
      };
      
      state.data = {
        first_name: payload.first_name || '',
        last_name: payload.last_name || '',
        date_of_birth: payload.date_of_birth || null,
        phone_number: payload.phone_number !== undefined ? payload.phone_number : '',
        email: payload.email !== undefined ? payload.email : '',
        gender: payload.gender !== undefined ? payload.gender : '',
        new_client_or_existing: payload.new_client_or_existing || 'new',
        referral: payload.referral || false,
        type_of_policy: payload.type_of_policy || '',
        life_insurance: getProductArray('Life insurance', 'life_insurance'),
        critical_illness: getProductArray('Critical Illness', 'critical_illness'),
        disability: getProductArray('Disability', 'disability'),
        segregated_funds: getProductArray('Segregated Funds', 'segregated_funds'),
        group_benefits: getProductArray('Group Benefits', 'group_benefits'),
        group_retirement_services: getProductArray('Group Retirement Services', 'group_retirement_services'),
        travel: getProductArray('Travel', 'travel'),
        individual_health_dental: getProductArray('Individual health and dental', 'individual_health_dental'),
        other: getProductArray('Other', 'other'),
        benefit_amount: payload.benefit_amount !== undefined ? payload.benefit_amount : '',
        premium_amount: payload.premium_amount !== undefined ? payload.premium_amount : '',
        ...(payload.monthly_annual && {
          monthly_annual: payload.monthly_annual,
        }),
        policy_number: payload.policy_number !== undefined ? payload.policy_number : '',
        ...(payload.term_rider !== undefined && {
          term_rider: payload.term_rider,
        }),
        ...(payload.carriers && { carriers: payload.carriers }),
        ...(payload.insurance_company && {
          insurance_company: payload.insurance_company,
        }),
        ...(payload.effective_date !== undefined && {
          effective_date: payload.effective_date,
        }),
        ...(payload.binder_policy_number && {
          binder_policy_number: payload.binder_policy_number,
        }),
        ...(payload.business_type && {
          business_type: payload.business_type,
        }),
        ...(payload.client_name && { client_name: payload.client_name }),
        ...(payload.customer_code && {
          customer_code: payload.customer_code,
        }),
        ...(payload.billing && { billing: payload.billing }),
        ...(payload.additional_broker_note !== undefined && {
          additional_broker_note: payload.additional_broker_note,
        }),
        disclosure_and_attestation: {
          broker_name: payload.disclosure_and_attestation?.broker_name || '',
        },
      };
      state.wipStatus = payload.wip_status || null;
      state.premiumConfirmed = payload.premium_confirmed || false;
      state.isDataSet = true;
      state.hasUnsavedChanges = false;
      state.isDataSaved = true;
      state.error = null;
      state.validationErrors = {};
      state.missingMandatoryFields = [];
    },
  },
});

export const {
  setClientFirstName,
  setClientLastName,
  setClientDateOfBirth,
  setClientPhoneNumber,
  setClientEmail,
  setClientGender,
  setClientType,
  setReferral,
  setTypeOfPolicy,
  updatePolicySection,
  updateProductAgnosticField,
  setPremiumConfirmed,
  setLoading,
  setSaving,
  setError,
  setValidationErrors,
  clearValidationErrors,
  setDataSet,
  setHasUnsavedChanges,
  changeIsDataSaved,
  setWipStatus,
  changeDisclosureAndAttestation,
  setAdditionalBrokerNote,
  addMissingMandatoryField,
  removeMissingMandatoryField,
  resetForm,
  initializeForm,
} = lifeInsuranceFormSlice.actions;

export default lifeInsuranceFormSlice.reducer;
